export const DEFAULT_CAPITAL_TOTAL = 96000
export const DEFAULT_MAX_MANUAL_PCT = 0.60
export const DEFAULT_HEDGE_FACTOR_K = 0.4

export function summarizeManualBook(ledger) {
  return Object.values(ledger || {})
    .filter((u) => u && u.bucket === 'manual')
    .reduce(
      (acc, u) => {
        const qty = Number(u.current?.qty || 0)
        const price = Number(u.current?.avgPrice || 0)
        acc.qty += qty
        acc.notional += price * qty
        return acc
      },
      { qty: 0, notional: 0 }
    )
}

export function summarizeFuturesSide(futuresInfos, side) {
  const pos = futuresInfos?.[side] || {}
  const qty = Number(pos.qty || 0)
  const price = Number(pos.price || 0)
  return {
    qty,
    notional: price * qty,
  }
}

export function summarizeShortBook(shortLedger) {
  return Object.values(shortLedger || {})
    .filter((u) => u && u.status === 'active')
    .reduce(
      (acc, u) => {
        const remainingQty = Number(u.remainingQty || 0)
        const sellPrice = Number(u.origin?.sellPrice || 0)
        acc.remainingQty += remainingQty
        acc.remainingNotional += remainingQty * sellPrice
        return acc
      },
      { remainingQty: 0, remainingNotional: 0 }
    )
}

export function computePmp(notional, qty) {
  return qty > 0 ? notional / qty : 0
}

export function computeHedgeAvg(notional, qty) {
  return computePmp(notional, qty)
}

export function computeCoverage(shortQty, manualQty) {
  return manualQty > 0 ? (shortQty / manualQty) * 100 : 0
}

export function computeHeadRoom({
  manualNotional,
  hedgeNotional,
  capitalTotal = DEFAULT_CAPITAL_TOTAL,
  maxManualPct = DEFAULT_MAX_MANUAL_PCT,
  k = DEFAULT_HEDGE_FACTOR_K,
} = {}) {
  const hf = manualNotional > 0 ? 1.0 + (hedgeNotional / manualNotional) * k : 1.0
  const maxAdj = capitalTotal * maxManualPct * hf
  return maxAdj > 0 ? ((maxAdj - manualNotional) / maxAdj) * 100 : 0
}

export function coverageTone(coverage) {
  if (coverage >= 100) return 'good'
  if (coverage >= 40) return 'warn'
  return 'bad'
}

export function computePortfolioBook({
  ledger,
  shortLedger,
  futuresInfos,
  includeFutures = false,
  markPrice = 0,
  capitalTotal = DEFAULT_CAPITAL_TOTAL,
  maxManualPct = DEFAULT_MAX_MANUAL_PCT,
  k = DEFAULT_HEDGE_FACTOR_K,
} = {}) {
  const manual = summarizeManualBook(ledger)
  const shorts = summarizeShortBook(shortLedger)
  const futLong = includeFutures ? summarizeFuturesSide(futuresInfos, 'long') : { qty: 0, notional: 0 }
  const futShort = includeFutures ? summarizeFuturesSide(futuresInfos, 'short') : { qty: 0, notional: 0 }

  const longQty = manual.qty + futLong.qty
  const longNotional = manual.notional + futLong.notional
  const shortQty = shorts.remainingQty + futShort.qty
  const hedgeNotional = shorts.remainingNotional + futShort.notional
  const coverage = computeCoverage(shortQty, longQty)
  const longsAvg = computePmp(longNotional, longQty)
  const hedgeAvg = computeHedgeAvg(hedgeNotional, shortQty)
  const longPnl = longQty * (markPrice - longsAvg)
  const shortPnl = shortQty * (hedgeAvg - markPrice)
  const netPnl = longPnl + shortPnl

  return {
    manualNotional: longNotional,
    manualQty: longQty,
    shortQty,
    hedgeNotional,
    includeFutures,
    pmp: longsAvg,
    hedgeAvg,
    coverage,
    coverageTone: coverageTone(coverage),
    longPnl,
    shortPnl,
    netPnl,
    headRoom: computeHeadRoom({
      manualNotional: longNotional,
      hedgeNotional,
      capitalTotal,
      maxManualPct,
      k,
    }),
  }
}
